/**
 * invoice-history.tsx — Cột "Hoá đơn" trong lịch sử thanh toán + hộp thoại "Yêu cầu xuất hoá đơn"
 * cho đơn đã thanh toán (lúc đặt chưa chọn xuất, hoặc lần trước lỗi).
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Download, FileText, Loader2, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { downloadOrderInvoice, useInvoiceProfiles, useRequestInvoice, type BillingOrder } from '@/hooks/use-billing'
import { useAuthStore } from '@/stores/auth-store'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { buildInvoiceBody, INVOICE_STATUS, type InvoiceChoice, type InvoiceErrors } from './invoice-info'
import { InvoiceSection, initialChoice } from './invoice-section'

export function InvoiceCell({ order, canPay, onRequest }: { order: BillingOrder; canPay: boolean; onRequest: (o: BillingOrder) => void }) {
  const { t } = useTranslation('billing')
  const inv = order.invoice
  const [busy, setBusy] = useState(false)
  if (!inv) return <span className="text-muted-foreground">—</span>
  // Đơn 0đ (quy đổi / mã ưu đãi trừ hết / gia hạn tay) không xuất hoá đơn (A9, lỗi L4 test 29/09).
  const canRequest = canPay && order.status === 'paid' && order.amount > 0 && (inv.status === 'none' || inv.status === 'failed')

  if (inv.status === 'none') {
    return canRequest
      ? <Button size="sm" variant="outline" onClick={() => onRequest(order)}><FileText className="h-3.5 w-3.5" /> {t('invoiceHistory.requestButton')}</Button>
      : <span className="text-muted-foreground">—</span>
  }
  const s = INVOICE_STATUS[inv.status] ?? INVOICE_STATUS.pending
  const download = async () => {
    setBusy(true)
    try {
      await downloadOrderInvoice(order.id, 'pdf')
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <span className="flex items-center gap-1.5">
        <Badge variant={s.variant} title={inv.error ?? undefined}>{s.text}</Badge>
        {inv.invoiceNo && <span className="font-mono text-xs">{t('invoiceHistory.no', { no: inv.invoiceNo })}</span>}
      </span>
      {inv.buyer && <span className="max-w-[180px] truncate text-xs text-muted-foreground" title={inv.buyer}>{inv.buyer}</span>}
      {inv.status === 'issued' && (
        <button type="button" onClick={download} disabled={busy} className="flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} {t('invoiceHistory.downloadPdf')}
        </button>
      )}
      {inv.status === 'failed' && canRequest && (
        <button type="button" onClick={() => onRequest(order)} className="flex items-center gap-1 text-xs text-primary hover:underline">
          <RefreshCw className="h-3 w-3" /> {t('invoiceHistory.resend')}
        </button>
      )}
    </div>
  )
}

export function RequestInvoiceDialog({ order, onClose }: { order: BillingOrder | null; onClose: () => void }) {
  const { t } = useTranslation('billing')
  const email = useAuthStore((s) => s.user?.email) ?? ''
  const profiles = useInvoiceProfiles(!!order)
  const request = useRequestInvoice()
  const [choice, setChoice] = useState<InvoiceChoice | null>(null)
  const [errors, setErrors] = useState<InvoiceErrors>({})

  useEffect(() => {
    if (!order) { setChoice(null); setErrors({}); return }
    if (!choice && !profiles.isLoading) setChoice({ ...initialChoice(profiles.data, email), enabled: true })
  }, [order, choice, profiles.isLoading, profiles.data, email])

  const submit = () => {
    if (!order || !choice) return
    const { body, errors: e } = buildInvoiceBody(choice)
    if (!body) { setErrors(e); return }
    request.mutate(
      { orderId: order.id, invoice: body },
      {
        onSuccess: () => {
          toast.success(t('invoiceHistory.toastSent'))
          onClose()
        },
        onError: (err) => toast.error(apiError(err)),
      },
    )
  }

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('invoiceHistory.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {order && <>{t('invoiceHistory.orderLine', { code: order.paymentCode ?? order.id.slice(0, 8), amount: formatVnd(order.amount) })} </>}
            {t('invoiceHistory.dialogDesc')}
          </DialogDescription>
        </DialogHeader>
        {choice ? (
          <InvoiceSection
            hideToggle
            choice={choice}
            onChange={(c) => { setChoice(c); setErrors({}) }}
            profiles={profiles.data ?? []}
            loadingProfiles={profiles.isLoading}
            errors={errors}
            disabled={request.isPending}
            defaultEmail={email}
          />
        ) : <p className="text-sm text-muted-foreground">{t('common:status.loading')}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={request.isPending}>{t('common:actions.cancel')}</Button>
          <Button onClick={submit} disabled={!choice || request.isPending}>
            {request.isPending && <Loader2 className="animate-spin" />} {t('invoiceHistory.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
